/**
 * Orion Console UI translations (en / ar).
 * Use t(key) or t(key, { param: value }) for interpolation.
 */
export const translations = {
  en: {
    // Header & nav
    skipToMainContent: 'Skip to main content',
    offlineMessage: 'You are offline. Requests will fail until connection is restored.',
    consoleTitle: 'Orion',
    consoleVersion: 'Console v2',
    consoleSubtitle: 'Financial Intelligence Engine',
    menu: 'Menu',
    newChat: 'New chat',
    history: 'History',
    selectBranch: 'Select branch',
    branchDefault: 'Branch',
    user: 'User',
    logOut: 'Log out',

    // Language switcher
    language: 'Language',
    langEn: 'English',
    langAr: 'العربية',

    // Sidebar
    conversations: 'Conversations',
    closeHistory: 'Close history',
    loading: 'Loading…',
    noPastConversations: 'No past conversations.',
    historyEmptyHint: 'Start a conversation below; it will appear here.',

    // Confirm dialogs
    confirmNewChatTitle: 'Start new chat?',
    confirmNewChatMessage: 'Your current conversation will remain in History.',
    confirmLogoutTitle: 'Log out?',
    confirmLogoutMessage: 'You will need to sign in again to continue.',
    confirm: 'Confirm',
    retry: 'Retry',
    newMessage: 'New message ↓',

    // Welcome
    welcomeLine: 'Ask Orion anything about your business: sales, inventory, finances, or tasks.',
    welcomeHint: 'Press Enter to send, Shift+Enter for new line.',
    welcomeNew: 'New: tool-results tables, tools-used chips, and approval/duplicate actions show up after Orion replies.',

    // Suggested prompts
    suggestedTodaySales: "Today's sales summary",
    suggestedLowStock: 'Low stock items',
    suggestedOpenTasks: 'Open Orion tasks',
    suggestedProfitability: 'Profitability snapshot',

    // Messages
    you: 'You',
    orion: 'Orion',
    orionThinking: 'Orion is thinking…',
    basedOn: 'Based on:',
    allBranches: 'All',
    noResponse: '(No response from Orion.)',
    responseCouldNotBeDisplayed: 'Response could not be displayed.',

    // Tool results
    toolDataResults: 'Tool data & results',
    tool: 'Tool',
    args: 'Args',
    result: 'Result',
    period: 'Period',
    fullJson: 'Full JSON',
    noResult: 'No result',
    copyJson: 'Copy JSON',
    showingFirstN: 'Showing first {{count}} of {{total}}',

    // Approval / duplicate
    actionRequiresApproval: 'Action requires your approval.',
    actionRequiresApprovalShort: 'Action requires approval',
    approve: 'Approve',
    cancel: 'Cancel',
    operationCancelled: 'Operation cancelled.',
    duplicateDetected: 'Duplicate action detected. Run again?',
    runAgain: 'Run again',
    okNotRunningAgain: 'Ok — not running it again.',
    thisActionJustExecuted: 'This action was just executed. Run again?',

    // Status / errors
    scrollToBottom: 'Scroll to bottom',
    messageOrion: 'Message Orion',
    send: 'Send',
    placeholderSend: 'Press Enter to send, Shift+Enter for new line',
    couldNotLoadConversation: 'Could not load conversation.',
    aiLimitReached: 'AI monthly limit reached. Please try again later or contact support.',
    aiLimitReachedOpenTicket: 'Open support ticket',
    supportTicketTitle: 'Create support ticket',
    supportTicketSubject: 'Subject',
    supportTicketMessage: 'Message',
    supportTicketPriority: 'Priority',
    supportTicketAttachmentsOptional: 'Attachments (optional)',
    supportTicketSubmit: 'Create ticket',
    supportTicketSubmitting: 'Creating…',
    supportTicketCreated: 'Ticket created. We\'ll get back to you soon.',
    placeholderTicketSubject: 'Short summary of the issue',
    placeholderTicketMessage: 'Describe the issue…',
    supportTicketPriorityLow: 'Low',
    supportTicketPriorityMedium: 'Medium',
    supportTicketPriorityHigh: 'High',
    tooManyRequests: 'Too many requests. Please wait a minute before sending again.',
    serviceUnavailable: 'Service is temporarily unavailable. Please try again in a moment.',
    operationCompleted: 'Operation completed successfully.',
    operationCompletedShort: 'Operation completed.',
    operationFailed: 'Operation failed: {{message}}',
    actionExecuted: 'Action executed.',
    approvalRequired: 'Approval required.',
    approvalMissingExecutionId: 'Action could not be completed — please try again.',
    approvalExpired: 'Approval expired. Please try again.',
    orionReplied: 'Orion replied.',
    messageSent: 'Message sent.',
    retrying: 'Retrying…',
    retryFailed: 'Retry failed: {{message}}',
    toolsUsedLabel: 'Tools used',

    export: 'Export',
    exportPdf: 'PDF',
    exportExcel: 'Excel',
    noMessagesToExport: 'No messages to export.',
    pdfDownloaded: 'PDF downloaded',
    excelDownloaded: 'Excel downloaded',
    exportFailed: 'Export failed',
    copyConversation: 'Copy conversation',
    scheduled: 'Scheduled',
    scheduledQuestions: 'Scheduled questions',
    scheduleQuestion: 'Schedule question',
    runAt: 'Run at',
    scheduledSaved: 'Scheduled.',
    scheduledDeleted: 'Deleted.',
    usageUsed: '{{pct}}% used',
    usageNearLimit: 'Near limit',

    copy: 'Copy',
    copyMarkdown: 'Copy (Markdown)',
    copyPlain: 'Copy as plain text',
    copied: 'Copied to clipboard',
    copiedPlain: 'Copied as plain text',

    attach: 'Attach',
    attachmentsLabel: 'Attachments',
    attachFile: 'Attach file or image',
    attachFileTitle: 'Attach image or file for analysis',
    removeAttachment: 'Remove attachment',
    fileAttached: 'File attached',
    uploadFailed: 'Upload failed',
    fileTooLarge: 'File too large. Maximum size is 100 MB.',
    attachedFiles: '(attached files)',
    packingListFiles: 'Packing list files',

    voiceInput: 'Voice input',
    voiceInputStop: 'Voice input (press to stop)',
    microphoneUnavailable: 'Microphone not available',

    help: 'Help',
    closeHelp: 'Close help',
    slashHelpIntro: 'Type a slash command in the input to run a quick action:',
    slashHelpHint: 'Example: /sales for today\'s sales, /inventory for stock issues.',
    placeholderSendSlash: 'e.g. /sales, /expenses… or type your question',

    settings: 'Settings',
    providerSettingsTitle: 'AI provider settings',
    closeSettings: 'Close settings',
    aiEnabled: 'AI enabled',
    aiProvider: 'Provider',
    aiApiKey: 'API key',
    aiApiKeyPlaceholder: 'Leave blank to keep current',
    showApiKey: 'Show API key',
    hideApiKey: 'Hide API key',
    aiApiBaseUrl: 'API base URL',
    aiDefaultModel: 'Default model',
    saveSettings: 'Save',
    saving: 'Saving…',
    settingsSaved: 'Settings saved.',
    creativeMode: 'Creative mode',
    creativeModeHint: 'Always use the AI model for replies (skip quick tool shortcuts).',
    userInstructions: 'Instructions for Orion',
    userInstructionsHint: 'Optional. E.g. "Always respond in Arabic" or "Keep answers brief". Max 2000 characters.',
    userInstructionsSaved: 'Instructions saved.',
    userInstructionsSaveFailed: 'Failed to save instructions.',
  },

  ar: {
    skipToMainContent: 'انتقل إلى المحتوى الرئيسي',
    offlineMessage: 'أنت غير متصل. الطلبات ستفشل حتى يعود الاتصال.',
    consoleTitle: 'أوريون',
    consoleVersion: 'وحدة التحكم v2',
    consoleSubtitle: 'محرك الذكاء المالي',
    menu: 'القائمة',
    newChat: 'محادثة جديدة',
    history: 'السجل',
    selectBranch: 'اختر الفرع',
    branchDefault: 'فرع',
    user: 'المستخدم',
    logOut: 'تسجيل الخروج',

    language: 'اللغة',
    langEn: 'English',
    langAr: 'العربية',

    conversations: 'المحادثات',
    closeHistory: 'إغلاق السجل',
    loading: 'جاري التحميل…',
    noPastConversations: 'لا توجد محادثات سابقة.',
    historyEmptyHint: 'ابدأ محادثة أدناه؛ ستظهر هنا.',

    confirmNewChatTitle: 'بدء محادثة جديدة؟',
    confirmNewChatMessage: 'ستبقى محادثتك الحالية في السجل.',
    confirmLogoutTitle: 'تسجيل الخروج؟',
    confirmLogoutMessage: 'ستحتاج لتسجيل الدخول مرة أخرى للمتابعة.',
    confirm: 'تأكيد',
    retry: 'إعادة المحاولة',
    newMessage: 'رسالة جديدة ↓',

    welcomeLine: 'اسأل أوريون أي شيء عن عملك: المبيعات، المخزون، المالية، أو المهام.',
    welcomeHint: 'Enter للإرسال، Shift+Enter لسطر جديد.',
    welcomeNew: 'جديد: جداول نتائج الأدوات، وشرائح الأدوات المستخدمة، وموافقة/تكرار تظهر بعد رد أوريون.',

    suggestedTodaySales: 'ملخص مبيعات اليوم',
    suggestedLowStock: 'عناصر منخفضة المخزون',
    suggestedOpenTasks: 'مهام أوريون المفتوحة',
    suggestedProfitability: 'لقطة الربحية',

    you: 'أنت',
    orion: 'أوريون',
    orionThinking: 'أوريون يفكر…',
    basedOn: 'بناءً على:',
    allBranches: 'الكل',
    noResponse: '(لا رد من أوريون.)',
    responseCouldNotBeDisplayed: 'تعذّر عرض الرد.',

    toolDataResults: 'بيانات الأدوات والنتائج',
    tool: 'أداة',
    args: 'المعطيات',
    result: 'النتيجة',
    period: 'الفترة',
    fullJson: 'JSON كامل',
    noResult: 'لا نتيجة',
    copyJson: 'نسخ JSON',
    showingFirstN: 'عرض أول {{count}} من {{total}}',

    actionRequiresApproval: 'الإجراء يتطلب موافقتك.',
    actionRequiresApprovalShort: 'الإجراء يتطلب موافقة',
    approve: 'موافقة',
    cancel: 'إلغاء',
    operationCancelled: 'تم إلغاء العملية.',
    duplicateDetected: 'تم اكتشاف إجراء مكرر. تشغيل مرة أخرى؟',
    runAgain: 'تشغيل مرة أخرى',
    okNotRunningAgain: 'حسناً — لن يتم تشغيله مرة أخرى.',
    thisActionJustExecuted: 'تم تنفيذ هذا الإجراء للتو. تشغيل مرة أخرى؟',

    scrollToBottom: 'التمرير للأسفل',
    messageOrion: 'رسالة لأوريون',
    send: 'إرسال',
    placeholderSend: 'Enter للإرسال، Shift+Enter لسطر جديد',
    couldNotLoadConversation: 'تعذر تحميل المحادثة.',
    aiLimitReached: 'تم الوصول للحد الشهري للذكاء الاصطناعي. يرجى المحاولة لاحقاً أو الاتصال بالدعم.',
    aiLimitReachedOpenTicket: 'فتح تذكرة دعم',
    supportTicketTitle: 'إنشاء تذكرة دعم',
    supportTicketSubject: 'الموضوع',
    supportTicketMessage: 'الرسالة',
    supportTicketPriority: 'الأولوية',
    supportTicketAttachmentsOptional: 'مرفقات (اختياري)',
    supportTicketSubmit: 'إنشاء تذكرة',
    supportTicketSubmitting: 'جاري الإنشاء…',
    supportTicketCreated: 'تم إنشاء التذكرة. سنرد عليك قريباً.',
    placeholderTicketSubject: 'ملخص قصير للمشكلة',
    placeholderTicketMessage: 'اكتب تفاصيل المشكلة…',
    supportTicketPriorityLow: 'منخفضة',
    supportTicketPriorityMedium: 'متوسطة',
    supportTicketPriorityHigh: 'عالية',
    tooManyRequests: 'طلبات كثيرة. يرجى الانتظار دقيقة قبل الإرسال مرة أخرى.',
    serviceUnavailable: 'الخدمة غير متاحة مؤقتاً. يرجى المحاولة بعد قليل.',
    operationCompleted: 'تمت العملية بنجاح.',
    operationCompletedShort: 'تمت العملية.',
    operationFailed: 'فشلت العملية: {{message}}',
    actionExecuted: 'تم تنفيذ الإجراء.',
    approvalRequired: 'مطلوبة موافقة.',
    approvalMissingExecutionId: 'لا يمكن تنفيذ الإجراء — يرجى إعادة المحاولة.',
    approvalExpired: 'انتهت صلاحية الموافقة. يرجى المحاولة مرة أخرى.',
    orionReplied: 'رد أوريون.',
    messageSent: 'تم إرسال الرسالة.',
    retrying: 'إعادة المحاولة…',
    retryFailed: 'فشلت إعادة المحاولة: {{message}}',
    toolsUsedLabel: 'الأدوات المستخدمة',

    export: 'تصدير',
    exportPdf: 'PDF',
    exportExcel: 'Excel',
    noMessagesToExport: 'لا توجد رسائل لتصديرها.',
    pdfDownloaded: 'تم تنزيل PDF',
    excelDownloaded: 'تم تنزيل Excel',
    exportFailed: 'فشل التصدير',
    copyConversation: 'نسخ المحادثة',
    scheduled: 'مجدول',
    scheduledQuestions: 'أسئلة مجدولة',
    scheduleQuestion: 'جدولة سؤال',
    runAt: 'تشغيل في',
    scheduledSaved: 'تم الحفظ.',
    scheduledDeleted: 'تم الحذف.',
    usageUsed: '{{pct}}% مستخدم',
    usageNearLimit: 'قريب من الحد',

    copy: 'نسخ',
    copyMarkdown: 'نسخ (Markdown)',
    copyPlain: 'نسخ كنص عادي',
    copied: 'تم النسخ',
    copiedPlain: 'تم النسخ كنص عادي',

    attach: 'إرفاق',
    attachmentsLabel: 'المرفقات',
    attachFile: 'إرفاق ملف أو صورة',
    attachFileTitle: 'إرفاق صورة أو ملف للتحليل',
    removeAttachment: 'إزالة المرفق',
    fileAttached: 'تم إرفاق الملف',
    uploadFailed: 'فشل الرفع',
    fileTooLarge: 'الملف كبير جداً. الحد الأقصى 100 ميجابايت.',
    attachedFiles: '(ملفات مرفقة)',
    packingListFiles: 'ملفات قوائم التعبئة',

    voiceInput: 'إدخال صوتي',
    voiceInputStop: 'إدخال صوتي (اضغط للإيقاف)',
    microphoneUnavailable: 'المايك غير متاح',

    help: 'المساعدة',
    closeHelp: 'إغلاق المساعدة',
    slashHelpIntro: 'اكتب أمراً يبدأ بـ / لتنفيذ إجراء سريع:',
    slashHelpHint: 'مثال: /sales لمبيعات اليوم، /inventory لحالة المخزون.',
    placeholderSendSlash: 'مثلاً /sales، /expenses… أو اكتب سؤالك',

    settings: 'الإعدادات',
    providerSettingsTitle: 'إعدادات مزود الذكاء الاصطناعي',
    closeSettings: 'إغلاق الإعدادات',
    aiEnabled: 'تفعيل الذكاء الاصطناعي',
    aiProvider: 'المزود',
    aiApiKey: 'مفتاح API',
    aiApiKeyPlaceholder: 'اتركه فارغاً للاحتفاظ بالحالي',
    showApiKey: 'إظهار المفتاح',
    hideApiKey: 'إخفاء المفتاح',
    aiApiBaseUrl: 'رابط API الأساسي',
    aiDefaultModel: 'النموذج الافتراضي',
    saveSettings: 'حفظ',
    saving: 'جاري الحفظ…',
    settingsSaved: 'تم حفظ الإعدادات.',
    creativeMode: 'الوضع الإبداعي',
    creativeModeHint: 'استخدام نموذج الذكاء الاصطناعي دائماً للردود (تخطي اختصارات الأدوات).',
    userInstructions: 'تعليمات لأوريون',
    userInstructionsHint: 'اختياري. مثلاً: "رد دائماً بالعربية" أو "اجعل الإجابات مختصرة". بحد أقصى 2000 حرف.',
    userInstructionsSaved: 'تم حفظ التعليمات.',
    userInstructionsSaveFailed: 'فشل حفظ التعليمات.',
  },
};

const STORAGE_KEY = 'orion_console_locale';
const DEFAULT_LOCALE = 'en';

function prefersArabic() {
  if (typeof navigator === 'undefined') return false;
  const langs = navigator.languages ?? [navigator.language ?? ''];
  return langs.some((l) => (l || '').toLowerCase().startsWith('ar'));
}

export function getStoredLocale() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'en' || stored === 'ar') return stored;
    if (prefersArabic()) return 'ar';
  } catch (_) {}
  return DEFAULT_LOCALE;
}

export function setStoredLocale(locale) {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch (_) {}
}

export function t(locale, key, params = {}) {
  const strings = translations[locale] || translations.en;
  let text = strings[key];
  if (text == null) text = translations.en[key] || key;
  Object.keys(params).forEach((k) => {
    text = text.replace(new RegExp(`{{${k}}}`, 'g'), String(params[k]));
  });
  return text;
}
